import React, { useState, useEffect, useCallback } from 'react';
import api from '../../api/axiosConfig';
import './TramitesCliente.css';

const TramitesCliente = ({ entidadId, cuit, addToast, initialExpandId, onExpanded }) => {
  const [activeTab, setActiveTab] = useState('actuales');
  const [tramites, setTramites] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedId, setExpandedId] = useState(null);

  const [plantillasDisponibles, setPlantillasDisponibles] = useState([]);
  const [loadingPlantillas, setLoadingPlantillas] = useState(false);

  const entidad = entidadId || null;

  const fetchTramites = useCallback(async () => {
    if (!entidad) return;
    setLoading(true);
    try {
      const res = await api.post('/obtenerTramitesUsuario', { entidadId: entidad });
      setTramites(res.data || []);
    } catch (err) {
      console.error('Error al obtener trámites:', err);
    } finally {
      setLoading(false);
    }
  }, [entidad]);

  const fetchPlantillasDisponibles = useCallback(async () => {
    if (!entidad) return;
    setLoadingPlantillas(true);
    try {
      const res = await api.post('/obtenerPlantillasInicioUsuario');
      setPlantillasDisponibles(res.data || []);
    } catch (err) {
      console.error('Error al obtener plantillas:', err);
    } finally {
      setLoadingPlantillas(false);
    }
  }, []);

  useEffect(() => {
    fetchTramites();
  }, [fetchTramites]);

  useEffect(() => {
    if (!loading && initialExpandId && tramites.some(p => p.id === initialExpandId)) {
      setActiveTab('actuales');
      setExpandedId(initialExpandId);
      if (onExpanded) onExpanded();
    }
  }, [loading, initialExpandId, tramites, onExpanded]);

  const handleIniciarTramite = async (plantillaId) => {
    try {
      await api.post('/iniciarTramiteCliente', { plantillaId, entidadId: entidad });
      addToast('Trámite iniciado correctamente');
      fetchTramites();
      setActiveTab('actuales');
    } catch (err) {
      const msg = err.response?.data?.error || 'Error al iniciar el trámite';
      addToast(msg, 'error');
    }
  };

  const calcularProgreso = (pasos) => {
    if (!pasos || pasos.length === 0) return 0;
    const completados = pasos.filter(p => p.completado).length;
    return Math.round((completados / pasos.length) * 100);
  };

  if (!entidad) {
    return (
      <div className="dashboard-content">
        <div className="page-header">
          <h1 className="page-title">Mis Trámites</h1>
          <p className="page-subtitle">Seguimiento de tus tramites y procesos</p>
        </div>
        <div className="pcc-empty">
          <div className="pcc-empty-icon">
            <i className="bi bi-person-badge"></i>
          </div>
          <h3>Selecciona una entidad</h3>
          <p>Elige una entidad desde el selector superior para ver sus trámites.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="dashboard-content">
      <div className="page-header">
        <h1 className="page-title">Mis Trámites</h1>
        <p className="page-subtitle">Seguimiento de tus tramites y procesos</p>
      </div>

      <div className="tc-tabs">
        <button className={`tc-tab ${activeTab === 'actuales' ? 'active' : ''}`} onClick={() => setActiveTab('actuales')}>
          <i className="bi bi-list-check"></i> Trámites Actuales
        </button>
        <button className={`tc-tab ${activeTab === 'iniciar' ? 'active' : ''}`} onClick={() => { setActiveTab('iniciar'); fetchPlantillasDisponibles(); }}>
          <i className="bi bi-play-circle"></i> Iniciar Trámite
        </button>
      </div>

      {activeTab === 'actuales' && renderActuales()}
      {activeTab === 'iniciar' && renderIniciar()}
    </div>
  );

  function renderActuales() {
    if (loading) {
      return (
        <div className="pcc-loading">
          <div className="pcc-spinner"></div>
          <p>Cargando tus trámites...</p>
        </div>
      );
    }

    if (tramites.length === 0) {
      return (
        <div className="pcc-empty">
          <div className="pcc-empty-icon">
            <i className="bi bi-clipboard-check"></i>
          </div>
          <h3>Sin trámites activos</h3>
          <p>Actualmente no tienes ningún trámite en curso.</p>
          <p className="pcc-empty-sub">
            Cuando el estudio inicie un tramite para vos, aparecera aqui.
          </p>
        </div>
      );
    }

    return (
      <div className="pcc-timeline">
        {tramites.map((proc) => {
          const progreso = calcularProgreso(proc.pasos);
          const estaCompletado = proc.estado_general === 'Completado';
          const isExpanded = expandedId === proc.id;

          return (
            <div
              key={proc.id}
              className={`pcc-card ${estaCompletado ? 'pcc-completado' : ''} ${isExpanded ? 'pcc-expanded' : ''}`}
              onClick={() => setExpandedId(isExpanded ? null : proc.id)}
            >
              <div className="pcc-card-top">
                <div className="pcc-card-icon">
                  <i className={`bi ${estaCompletado ? 'bi-check2-circle' : 'bi-arrow-right-circle'}`}></i>
                </div>
                <div className="pcc-card-body">
                  <div className="pcc-card-title-row">
                    <h3>{proc.plantilla_nombre}</h3>
                    <span className={`pcc-badge ${estaCompletado ? 'pcc-badge-done' : 'pcc-badge-pending'}`}>
                      {estaCompletado ? 'Completado' : 'En curso'}
                    </span>
                  </div>
                  <p className="pcc-desc">{proc.plantilla_descripcion}</p>
                  <div className="pcc-meta">
                    <span><i className="bi bi-calendar3"></i> Iniciado: {new Date(proc.iniciado_en).toLocaleDateString('es-AR', { day: 'numeric', month: 'long', year: 'numeric' })}</span>
                  </div>
                </div>
                <div className="pcc-progress-wrap">
                  <div className="pcc-ring">
                    <svg viewBox="0 0 36 36" className="pcc-ring-svg">
                      <path className="pcc-ring-bg"
                        d="M18 2.0845 a 15.9155 15.9155 0 0 1 0 31.831 a 15.9155 15.9155 0 0 1 0 -31.831"
                      />
                      <path className="pcc-ring-fill"
                        strokeDasharray={`${progreso}, 100`}
                        d="M18 2.0845 a 15.9155 15.9155 0 0 1 0 31.831 a 15.9155 15.9155 0 0 1 0 -31.831"
                      />
                      <text x="18" y="19" className="pcc-ring-text">{progreso}%</text>
                    </svg>
                  </div>
                </div>
              </div>

              {isExpanded && (
                <div className="pcc-card-detail">
                  <div className="pcc-steps-header">
                    <i className="bi bi-list-ol"></i> Pasos del trámite
                  </div>
                  <div className="pcc-steps">
                    {proc.pasos.map((paso, index) => (
                      <div
                        key={paso.id}
                        className={`pcc-step ${paso.completado ? 'pcc-step-done' : ''} ${index === proc.pasos.findIndex(p => !p.completado) && !estaCompletado ? 'pcc-step-current' : ''}`}
                      >
                        <div className="pcc-step-indicator">
                          {paso.completado ? (
                            <div className="pcc-step-dot pcc-dot-done">
                              <i className="bi bi-check-lg"></i>
                            </div>
                          ) : index === proc.pasos.findIndex(p => !p.completado) && !estaCompletado ? (
                            <div className="pcc-step-dot pcc-dot-current">
                              <div className="pcc-dot-pulse"></div>
                            </div>
                          ) : (
                            <div className="pcc-step-dot pcc-dot-pending">{index + 1}</div>
                          )}
                          {index < proc.pasos.length - 1 && <div className={`pcc-step-line ${paso.completado ? 'pcc-line-done' : ''}`} />}
                        </div>
                        <div className="pcc-step-content">
                          <div className="pcc-step-title">{paso.titulo_paso}</div>
                          <div className="pcc-step-desc">{paso.descripcion_paso}</div>
                          {paso.fecha_completado && (
                            <div className="pcc-step-date">
                              <i className="bi bi-check-circle-fill"></i>
                              Completado el {new Date(paso.fecha_completado).toLocaleDateString('es-AR', { day: 'numeric', month: 'long' })}
                            </div>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                  {estaCompletado && (
                    <div className="pcc-completed-banner">
                      <i className="bi bi-trophy-fill"></i> Trámite completado exitosamente
                    </div>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>
    );
  }

  function renderIniciar() {
    if (loadingPlantillas) {
      return (
        <div className="pcc-loading">
          <div className="pcc-spinner"></div>
          <p>Cargando trámites disponibles...</p>
        </div>
      );
    }

    if (plantillasDisponibles.length === 0) {
      return (
        <div className="pcc-empty">
          <div className="pcc-empty-icon">
            <i className="bi bi-inbox"></i>
          </div>
          <h3>No hay trámites disponibles</h3>
          <p>Actualmente no hay trámites que puedas iniciar por tu cuenta.</p>
        </div>
      );
    }

    return (
      <div className="plantillas-disponibles-grid">
        {plantillasDisponibles.map(p => (
          <div key={p.id} className="plantilla-disponible-card">
            <div className="pdc-header">
              <i className="bi bi-file-earmark-text"></i>
              <h4>{p.nombre}</h4>
            </div>
            <p className="pdc-desc">{p.descripcion}</p>
            <div className="pdc-meta">
              <span><i className="bi bi-list-ol"></i> {p.total_pasos} pasos</span>
            </div>
            <button className="btn btn-primary pdc-btn" onClick={() => handleIniciarTramite(p.id)}>
              <i className="bi bi-play-fill"></i> Iniciar Trámite
            </button>
          </div>
        ))}
      </div>
    );
  }
};

export default TramitesCliente;
